import { BrowserModule } from '@angular/platform-browser';
import { NgModule } from '@angular/core';

import { FormsModule, ReactiveFormsModule } from '@angular/forms';

import { AppRoutingModule } from './app-routing.module';
import { HttpClientModule } from '@angular/common/http';

import { AppComponent } from './app.component';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { NgxChartsModule }from '@swimlane/ngx-charts';

import { CompaniesComponent } from './companies/companies.component';
import { CompanyDetailComponent } from './components/company-detail/company-detail.component';
import { MessagesComponent } from './messages/messages.component';
import { DashboardComponent } from './dashboard/dashboard.component';
import { LoginTwoComponent } from './login-two/login-two.component';

import { HttpClientInMemoryWebApiModule } from 'angular-in-memory-web-api';
import { InMemoryDataService } from './in-memory-data.service';

import { MatSliderModule } from '@angular/material/slider';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatInputModule } from '@angular/material/input';
import { MatDialogModule } from '@angular/material/dialog';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatButtonModule } from '@angular/material/button';
import { MatSelectModule } from '@angular/material/select';
import { MatMenuModule } from '@angular/material/menu';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatRadioModule } from '@angular/material/radio';
import { MatCardModule } from '@angular/material/card';
import { MatTabsModule } from '@angular/material/tabs';
import { MatStepperModule } from '@angular/material/stepper';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { NavigationComponent } from './navigation/navigation.component';
import { LayoutModule } from '@angular/cdk/layout';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatListModule } from '@angular/material/list';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule, MAT_DATE_LOCALE } from '@angular/material/core';
import { TableComponent } from './table/table.component';
import { MatTableModule } from '@angular/material/table';
import { MatDividerModule } from '@angular/material/divider';
import { MatPaginatorModule } from '@angular/material/paginator';
import { MatSortModule } from '@angular/material/sort';
import { LoginComponent } from './components/login/login.component';
import { CompanyListComponent } from './components/company-list/company-list.component';
import { TreeComponent } from './tree/tree.component';
import { MatTreeModule } from '@angular/material/tree';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ForgotPasswordComponent } from './components/forgot-password/forgot-password.component';
import { HomeComponent } from './components/home/home.component';
import { LineGraphComponent } from './components/line-graph/line-graph.component';
import { Globals } from './globals';
import { AddCompanyComponent } from './components/add-company/add-company.component';
import { EditThemeComponent } from './components/edit-theme/edit-theme.component';
import { UserListComponent } from './components/user-list/user-list.component';
import { CompanyStatsComponent } from './components/company-stats/company-stats.component';
import { AddUserComponent } from './components/add-user/add-user.component';
import { MatchingValidatorDirective } from './components/add-user/matching-validator.directive';
import { MyAccountComponent } from './components/my-account/my-account.component';
import { MyCompanyComponent } from './components/my-company/my-company.component';
import { EditPasswordComponent } from './components/edit-password/edit-password.component';
import { ResetPasswordComponent } from './components/reset-password/reset-password.component';
import { CompanyCheckerComponent } from './components/company-checker/company-checker.component'
import { Config } from './config';
import { ResourcesComponent } from './components/resources/resources.component';
import { ContactUsComponent } from './components/contact-us/contact-us.component';
import { CsvDownloadDialogComponent } from './components/csv-download-dialog/csv-download-dialog.component';
import { CsvUpdateDialogComponent } from './components/csv-update-dialog/csv-update-dialog.component';
import { DetailDeveloperComponent } from './components/detail-developer/detail-developer.component';
import { CompanyBillingComponent } from './components/company-billing/company-billing.component';
import { CompanyAdvancedComponent } from './components/company-advanced/company-advanced.component';
import { DeveloperComponent } from './components/developer/developer.component';
import { ConfirmDialogComponent } from './components/confirm-dialog/confirm-dialog.component';
import { CompanySetupComponent } from './components/company-setup/company-setup.component';
import { CompanySetupProcessComponent } from './components/company-setup-process/company-setup-process.component';
import { CompanySetupPageComponent } from './components/company-setup-page/company-setup-page.component';
import { SetupMicrosoftComponent } from './components/setup-microsoft/setup-microsoft.component';
import { SetupGoogleComponent } from './components/setup-google/setup-google.component';
import { SetupSpfComponent } from './components/setup-spf/setup-spf.component';
import { SetupDkimComponent } from './components/setup-dkim/setup-dkim.component';
import { SetupRoundtripComponent } from './components/setup-roundtrip/setup-roundtrip.component';
import { SetupCompleteComponent } from './components/setup-complete/setup-complete.component';
import { SetupSteps } from './setupSteps';
import { ProvisionCompleteComponent } from './components/provision-complete/provision-complete.component';
import { BillingService } from './components/company-billing/billing.service';
import { CompanyLogoComponent } from './components/company-logo/company-logo.component';
import { NotificationsComponent } from './components/notifications/notifications.component';
import { RemoveLogoComponent } from './components/company-logo/remove-logo/remove-logo.component';
import { PreferencesComponent } from './components/notifications/preferences/preferences.component';
import { CompanyHierarchyComponent } from './components/company-hierarchy/company-hierarchy.component';
import { Error404Component } from './components/error404/error404.component';
import { ConfirmPopupComponent } from './components/confirm-popup/confirm-popup.component';
import { BillingComponent } from './components/billing/billing.component';
import { LineGraph2Component } from './components/line-graph2/line-graph2.component';
import { CompanyConfigComponent } from './components/company-config/company-config.component';
import { FroalaEditorModule, FroalaViewModule } from 'angular-froala-wysiwyg';
import { ComposeComponent } from './components/compose/compose.component';
import { SpSpinnerComponent } from './components/sp-spinner/sp-spinner.component';
import { DialogConfirmComponent } from './components/dialog-confirm/dialog-confirm.component';
import { DnsConfigComponent } from './components/dns-config/dns-config.component';
import { UserManagementComponent } from './components/user-management/user-management.component';
import { DialogWelcomeComponent } from './components/dialog-welcome/dialog-welcome.component';
import { DeleteSetupComponent } from './components/delete-setup/delete-setup.component';
import { TlsDialogComponent } from './components/tls-dialog/tls-dialog.component';

@NgModule({
  declarations: [
    AppComponent,
    CompaniesComponent,
    CompanyDetailComponent,
    MessagesComponent,
    DashboardComponent,
    LoginTwoComponent,
    NavigationComponent,
    TableComponent,
    LoginComponent,
    CompanyListComponent,
    TreeComponent,
    ForgotPasswordComponent,
    HomeComponent,
    LineGraphComponent,
    AddCompanyComponent,
    EditThemeComponent,
    UserListComponent,
    CompanyStatsComponent,
    AddUserComponent,
    MatchingValidatorDirective,
    MyAccountComponent,
    MyCompanyComponent,
    EditPasswordComponent,
    ResetPasswordComponent,
    CompanyCheckerComponent,
    ResourcesComponent,
    ContactUsComponent,
    CsvDownloadDialogComponent,
    CsvUpdateDialogComponent,
    DetailDeveloperComponent,
    CompanyBillingComponent,
    CompanyAdvancedComponent,
    DeveloperComponent,
    ConfirmDialogComponent,
    CompanySetupComponent,
    CompanySetupProcessComponent,
    CompanySetupPageComponent,
    SetupMicrosoftComponent,
    SetupGoogleComponent,
    SetupSpfComponent,
    SetupDkimComponent,
    SetupRoundtripComponent,
    SetupCompleteComponent,
    ProvisionCompleteComponent,
    CompanyLogoComponent,
    NotificationsComponent,
    RemoveLogoComponent,
    PreferencesComponent,
    CompanyHierarchyComponent,
    Error404Component,
    ConfirmPopupComponent,
    BillingComponent,
    LineGraph2Component,
    CompanyConfigComponent,
    ComposeComponent,
    SpSpinnerComponent,
    DialogConfirmComponent,
    DnsConfigComponent,
    UserManagementComponent,
    DialogWelcomeComponent,
    DeleteSetupComponent,
    TlsDialogComponent
  ],
  imports: [
    BrowserModule,
    AppRoutingModule,
    BrowserAnimationsModule,
    FormsModule,
    ReactiveFormsModule,
    AppRoutingModule,
    MatSliderModule,
    HttpClientModule,
    // The HttpClientInMemoryWebApiModule module intercepts HTTP requests
    // and returns simulated server responses.
    // Remove it when a real server is ready to receive requests.
    // HttpClientInMemoryWebApiModule.forRoot(
    //   InMemoryDataService, { dataEncapsulation: false }
    // ),
    NgxChartsModule,
    FroalaEditorModule.forRoot(), 
    FroalaViewModule.forRoot(),
    MatInputModule,
    MatButtonModule,
    MatSelectModule,
    MatRadioModule,
    MatMenuModule,
    MatCheckboxModule,
    MatCardModule,
    MatNativeDateModule,
    MatDividerModule,
    MatTooltipModule,
    MatExpansionModule,
    LayoutModule,
    MatToolbarModule,   
    MatButtonToggleModule, 
    MatProgressSpinnerModule,
    MatSidenavModule,
    MatIconModule,
    MatDatepickerModule,
    MatTabsModule,
    MatStepperModule,
    MatListModule,
    MatTableModule,
    MatPaginatorModule,
    MatSortModule,
    MatTreeModule,
    MatSnackBarModule,
    MatDialogModule
  ],
  providers: [Globals,SetupSteps,Config,BillingService,{ provide: MAT_DATE_LOCALE, useValue: 'en-GB' }],
  bootstrap: [AppComponent]
})
export class AppModule { }
